/**
 * CHECKOUT-HANDOFF-AND-DOCTOR-CONTEXT-V1 -- pure view-model helpers for the
 * doctor queue-context drawer (DOCTOR-CONTEXT LAW). Tested by direct call
 * (repo idiom: no jsdom).
 *
 * Everything renders from the ONE doctor-safe drawer read
 * (GET /api/doctor/queue-context/:appointmentId). Identity is honest by
 * construction: a named row shows its name; a nameless enquiry states the
 * REASON it has no name yet (never "Unknown patient" -- the #246 ban).
 */

import type { DoctorQueueContext } from '@/dashboard/api';
import {
  derivePaymentHandoff,
  NO_HANDOFF_ADVISED_ONLY_COPY,
  type PaymentHandoffState,
} from '@/dashboard/appointments/payment-handoff';
import { doctorConsultOpen } from '@/dashboard/ui/doctor-visit-state';
import { rupeesLabel } from '@/dashboard/doctor/doctor-consult-view';

/** The drawer title: the admin-plane name, or the honest pending state. */
export function drawerTitle(ctx: DoctorQueueContext): string {
  if (ctx.identity.nameLabel !== null) return ctx.identity.nameLabel;
  return ctx.identity.namePending ? 'Name pending' : 'Unnamed appointment';
}

/**
 * The name-pending REASON line (identity-or-reason law): why the row has no
 * name yet + what resolves it. Channel class is the marketing-free server
 * label; the next step adapts to where the visit stands.
 */
export function drawerPendingLine(ctx: DoctorQueueContext): string | null {
  if (!ctx.identity.namePending) return null;
  const channel = ctx.identity.pendingChannel ?? 'Enquiry';
  const status = ctx.appointment.status;
  const nextStep =
    status === 'booked' || status === 'confirmed'
      ? 'waiting for front-desk check-in'
      : 'front desk can register the patient from the enquiry record';
  return `${channel} - not registered yet - ${nextStep}`;
}

/** The visit-state chip (the SAME vocabulary the day board renders).
 *  DOCTOR-HOME-STATE-MODEL-V1 (F1): the in-consult rule is CONSUMED from
 *  the ONE state owner (doctorConsultOpen -- this was the fourth duplicated
 *  copy of the predicate), so the drawer, the board ops chip, and the home
 *  rows can never disagree on one visit. */
export function drawerVisitChip(ctx: DoctorQueueContext): {
  label: string;
  tone: 'positive' | 'pending' | 'neutral';
} {
  const v = ctx.visit.visitStatus;
  if (v === 'finalized') return { label: 'Visit finalized', tone: 'positive' };
  if (
    doctorConsultOpen({
      id: ctx.appointment.id,
      status: ctx.appointment.status,
      visitStatus: v,
      consultStarted: ctx.visit.consultStarted,
    })
  ) {
    return { label: 'In consultation', tone: 'pending' };
  }
  return { label: 'Visit not started', tone: 'neutral' };
}

/** ARRIVAL TRUTH (Scope 2a): the drawer's actual-arrival line -- the
 *  patient_arrived event's own timestamp as a SECOND fact next to the
 *  scheduled slot; null (no line) when no arrival was recorded. The caller
 *  formats the timestamp with the shared IST formatter. */
export function drawerArrivedAt(ctx: DoctorQueueContext): string | null {
  const at = ctx.appointment.arrivedAt;
  return typeof at === 'string' && at !== '' ? at : null;
}

/** The plan summary line: counts + the accepted sum -- never procedure
 *  names (the full plan lives behind the consult door). Null when no sheet
 *  or an empty plan. */
export function drawerPlanLine(ctx: DoctorQueueContext): string | null {
  const p = ctx.plan;
  if (p === null) return null;
  const parts: string[] = [];
  if (p.accepted > 0) {
    parts.push(
      p.acceptedValue !== null
        ? `${p.accepted} accepted (${rupeesLabel(p.acceptedValue)})`
        : `${p.accepted} accepted`,
    );
  }
  if (p.advised > 0) parts.push(`${p.advised} advised`);
  if (p.declined > 0) parts.push(`${p.declined} declined`);
  if (p.pending > 0) parts.push(`${p.pending} pending`);
  return parts.length === 0 ? null : parts.join(' - ');
}

/** The payment-handoff state for the drawer (the ONE shared derivation). */
export function drawerHandoff(ctx: DoctorQueueContext): PaymentHandoffState {
  const plan = ctx.plan;
  return derivePaymentHandoff({
    status: ctx.appointment.status,
    visitStatus: ctx.visit.visitStatus,
    paymentRecorded: ctx.payment.paymentRecorded,
    acceptedPlanCount: plan?.accepted ?? 0,
    advisedPlanCount: plan?.advised ?? 0,
    suggestedCollectAmount: ctx.payment.suggestedCollectAmount,
  });
}

/** The payment-handoff display for the drawer's payment region; null when no
 *  handoff state applies yet (pre-close -- the region does not render). */
export function drawerHandoffDisplay(ctx: DoctorQueueContext): {
  label: string;
  tone: 'positive' | 'pending' | 'info';
  detail: string | null;
} | null {
  const state = drawerHandoff(ctx);
  switch (state.kind) {
    case 'recorded':
      return { label: 'Payment recorded', tone: 'positive', detail: null };
    case 'collect':
      return {
        label: 'Payment pending',
        tone: 'pending',
        detail:
          state.amount !== null
            ? `${rupeesLabel(state.amount)} suggested - front desk collects`
            : 'Doctor accepted - no estimate recorded; front desk collects',
      };
    case 'advised_only':
      // tone 'info' = the day board's exact chip tone (adversarial finding
      // F6: one vocabulary, ONE color everywhere).
      return {
        label: 'Follow-up needed',
        tone: 'info',
        detail: NO_HANDOFF_ADVISED_ONLY_COPY,
      };
    default:
      return null;
  }
}
